import { makeStyles, Theme } from '@maas-components/styles';

const useStyles = makeStyles<Theme>(
    (theme: Theme) => {
        return {
            root: {
                alignItems: 'center',
                border: 'none',
                borderRadius: 32 / 2,
                boxSizing: 'border-box',
                cursor: 'default',
                display: 'inline-flex',
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.pxToRem(16),
                height: 32,
                justifyContent: 'center',
                outline: 0,
                padding: theme.spacing(0.5, 2),
                textDecoration: 'none',
                transition: theme.transitions.create(['background-color', 'box-shadow']),
                verticalAlign: 'middle',
                whiteSpace: 'nowrap',
                '&$disabled': {
                    opacity: 0.5,
                    pointerEvents: 'none',
                },
            },
            sizeSmall: {
                height: 24,
                fontSize: theme.typography.pxToRem(14),
            },
            clickable: {
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                cursor: 'pointer',
                '&:hover, &:focus-visible': {
                    opacity: 0.8,
                },
            },
            disabled: {},
            /* Styles applied to the root element if `variant="filled"`. */
            filled: {},
            /* Styles applied to the root element if `variant="dot"`. */
            dot: {
                padding: theme.spacing(0.5, 0),
                '&:before': {
                    content: '""',
                    flexGrow: 0,
                    flexShrink: 0,
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    margin: '4px 12px 4px 0',
                },
            },
            /* Styles applied to the root element if `variant="filled"`. */
            filledPrimary: {
                color: theme.palette.primary.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.primary.main,
            },
            /* Styles applied to the root element if `variant="filled"`. */
            filledSecondary: {
                color: theme.palette.secondary.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.secondary.main,
            },
            /* Styles applied to the root element if `variant="filled"`. */
            filledDefault: {
                color: theme.palette.grey[600],
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.grey[300],
            },
            /* Styles applied to the root element if `variant="filled"` and `severity="success"`. */
            filledSuccess: {
                color: theme.palette.success.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.success.main,
            },
            /* Styles applied to the root element if `variant="filled"` and `severity="info"`. */
            filledInfo: {
                color: theme.palette.info.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.info.main,
            },
            /* Styles applied to the root element if `variant="filled"` and `severity="warning"`. */
            filledWarning: {
                color: theme.palette.warning.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.warning.main,
            },
            /* Styles applied to the root element if `variant="filled"` and `severity="error"`. */
            filledError: {
                color: theme.palette.error.contrastText,
                fontWeight: theme.typography.fontWeightMedium as number,
                backgroundColor: theme.palette.error.main,
            },
            /* Styles applied to the root element if `variant="dot"`. */
            dotDefault: {
                '&:before': {
                    backgroundColor: theme.palette.grey[300],
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="primary"`. */
            dotPrimary: {
                '&:before': {
                    backgroundColor: theme.palette.primary.main,
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="secondary"`. */
            dotSecondary: {
                '&:before': {
                    backgroundColor: theme.palette.secondary.main,
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="success"`. */
            dotSuccess: {
                '&:before': {
                    backgroundColor: theme.palette.success.main,
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="info"`. */
            dotInfo: {
                '&:before': {
                    backgroundColor: theme.palette.info.main,
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="warning"`. */
            dotWarning: {
                '&:before': {
                    backgroundColor: theme.palette.warning.main,
                },
            },
            /* Styles applied to the root element if `variant="dot"` and `severity="error"`. */
            dotError: {
                '&:before': {
                    backgroundColor: theme.palette.error.main,
                },
            },
        };
    },
    { name: 'MuiStatusBullet' },
);

export default useStyles;
